import { Form } from 'formik';
import { assign, keys, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  BankSelect,
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikDialog,
  FormikTextField,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { BinDto } from 'types';

type Values = BinDto;

type Props = DialogProps<CloseFormikDialogResult<Values>>;

export const BankBinDetailsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.bins',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const title = useMemo(
    () => (data ? t('title.edit') : t('title.create')),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      bankId: '',
      bin: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        bankId: Yup.string().required(tCommon('errors.required')),
        bin: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const handleClose = useCallback(
    (data: CloseFormikDialogResult<Values>) => {
      if (!data.ok) {
        data.data?.formikHelpers?.resetForm();
      }
      onClose(data);
    },
    [onClose],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
      }}
      formikProps={{
        validationSchema,
        validateOnMount: true,
        initialValues,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <BankSelect name="bankId" required />
            <FormikTextField label={t('fields.bin')} name="bin" required />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
